import React, { forwardRef } from 'react';

import { Control, Tooltip } from '@vlabs/uikit';
import type { ButtonProps } from '@vlabs/uikit/components/controls/button/Button';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { viewerCan } from '@vlabs/pages/auth/selectors';

import type { TCan, TPermissions } from './types';

export interface ButtonWithPermissionsProps extends ButtonProps {
  permissions: TPermissions;
  disabled?: boolean;
  title?: string;
  icon?: React.ReactNode;
  children?: React.ReactNode | string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
}

function ButtonWithPermissions(
  {
    permissions,
    disabled = false,
    title = '',
    icon = null,
    children = undefined,
    ...props
  }: ButtonWithPermissionsProps,
  externalRef: React.ForwardedRef<HTMLDivElement>,
): React.JSX.Element {
  const { t } = useTranslation();
  const can: TCan = useSelector(viewerCan);

  const { rules = [], method = 'oneof' } = permissions ?? {};

  const hasPermission = rules.length ? can(rules, { method }) : true;
  const isDisabled = !hasPermission || disabled;

  const Component = (icon != null && !children) ? Control.IconButton : Control.Button;

  return (
    <Tooltip
      tooltipContent={!hasPermission ? t('кнопка.недостаточно прав.title') : null}
    >
      <Component
        disabled={isDisabled}
        icon={icon}
        ref={externalRef}
        title={title}
        type="button"
        {...props}
      >
        {children}
      </Component>
    </Tooltip>
  );
}

const ButtonWithPermissionsWithRef = forwardRef(ButtonWithPermissions);
export { ButtonWithPermissionsWithRef as ButtonWithPermissions };
